import { useCallback, useEffect, useRef, useState } from "react";
import { gameApi } from "@/lib/api";
import {
  getResultMessage,
  isSuccessResult,
  WALLET_VIEWS,
} from "../financeWallet.helpers";

const normalizeHistoryDeleteId = (value) =>
  value === null || value === undefined || String(value).trim() === "" ? null : value;

const getHistorySubject = (view) => {
  if (view === WALLET_VIEWS.DEPOSIT) return "입금";
  if (view === WALLET_VIEWS.WITHDRAW) return "출금";
  if (view === WALLET_VIEWS.POINT) return "포인트";
  return "콤프";
};

const runHistoryDelete = (view, { scope, id }) => {
  if (view === WALLET_VIEWS.DEPOSIT) {
    return scope === "all"
      ? gameApi.finance.deleteAllDepositHistory()
      : gameApi.finance.deleteDepositHistory({ id });
  }
  if (view === WALLET_VIEWS.WITHDRAW) {
    return scope === "all"
      ? gameApi.finance.deleteAllWithdrawalHistory()
      : gameApi.finance.deleteWithdrawalHistory({ id });
  }
  if (view === WALLET_VIEWS.POINT) {
    return scope === "all"
      ? gameApi.finance.deleteAllPointHistory()
      : gameApi.finance.deletePointHistory({ id });
  }
  return scope === "all"
    ? gameApi.finance.deleteAllCompHistory()
    : gameApi.finance.deleteCompHistory({ id });
};

const alwaysCurrent = () => true;

const useWalletHistoryDelete = ({
  isCurrentScope = alwaysCurrent,
  historyPage,
  isUsdtDepositMode,
  loadContent,
  notice,
  setHistoryPage,
  setNotice,
  showNotice,
  view,
}) => {
  const [isDeletePending, setIsDeletePending] = useState(false);
  const deleteRequestRef = useRef(false);
  const isMountedRef = useRef(true);
  const canDeleteHistory = !isUsdtDepositMode;
  useEffect(() => { setIsDeletePending(false); }, [isCurrentScope]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const getDeleteId = useCallback(
    (row) => {
      if (view === WALLET_VIEWS.DEPOSIT) {
        return normalizeHistoryDeleteId(row?.deposit?.id);
      }
      if (view === WALLET_VIEWS.WITHDRAW) {
        return normalizeHistoryDeleteId(row?.withdrawal?.id);
      }
      return normalizeHistoryDeleteId(row?.id);
    },
    [view]
  );

  const requestDelete = useCallback(
    (scope, deleteId = null) => {
      if (!isCurrentScope() || !canDeleteHistory || deleteRequestRef.current) return;
      if (scope === "row" && normalizeHistoryDeleteId(deleteId) === null) return;

      const subject = getHistorySubject(view);
      setNotice({
        type: "confirm",
        title: `${subject} 내역 삭제`,
        message:
          scope === "all"
            ? `${subject} 내역 전체를 화면에서 삭제하시겠습니까?`
            : `선택한 ${subject} 내역을 화면에서 삭제하시겠습니까?`,
        historyDelete: { scope, id: deleteId },
      });
    },
    [isCurrentScope, canDeleteHistory, setNotice, view]
  );

  const confirmDelete = useCallback(async () => {
    const deleteRequest = notice?.historyDelete;
    if (
      !isCurrentScope() ||
      !canDeleteHistory ||
      !deleteRequest ||
      deleteRequestRef.current ||
      (deleteRequest.scope === "row" &&
        normalizeHistoryDeleteId(deleteRequest.id) === null)
    ) {
      return;
    }

    deleteRequestRef.current = true;
    setIsDeletePending(true);
    try {
      const result = await runHistoryDelete(view, deleteRequest);
      if (!isCurrentScope()) return;
      if (!isSuccessResult(result)) {
        showNotice("error", getResultMessage(result, "내역 삭제에 실패했습니다."));
        return;
      }

      const targetPage = deleteRequest.scope === "all" ? 1 : historyPage;
      if (deleteRequest.scope === "all" && historyPage !== 1) setHistoryPage(1);
      const refreshed = await loadContent({ silent: true, pageOverride: targetPage });
      if (!isCurrentScope()) return;
      if (
        deleteRequest.scope === "row" &&
        historyPage > 1 &&
        refreshed?.success &&
        refreshed.rows.length === 0
      ) {
        setHistoryPage(historyPage - 1);
      }
      showNotice("success", getResultMessage(result, "내역이 삭제되었습니다."));
    } catch (error) {
      if (isCurrentScope()) showNotice("error", error?.message || "내역 삭제에 실패했습니다.");
    } finally {
      deleteRequestRef.current = false;
      if (isMountedRef.current) setIsDeletePending(false);
    }
  }, [isCurrentScope, canDeleteHistory, historyPage, loadContent, notice, setHistoryPage, showNotice, view]);

  const closeNotice = useCallback(() => {
    if (deleteRequestRef.current) return;
    setNotice(null);
  }, [setNotice]);

  return {
    canDeleteHistory,
    closeNotice,
    confirmDelete,
    getDeleteId,
    isDeletePending,
    requestDelete,
  };
};

export default useWalletHistoryDelete;
